<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

type ItemOption = {
  label: string
  value: string | number | boolean | null
  color?: string
  disabled?: boolean
}

const props = defineProps<{
  items: ItemOption[]
  modelValue: string | number | boolean | null
  color?: string
  variant?: 'flat' | 'elevated' | 'outlined' | 'tonal' | 'text' | 'plain'
  size?: 'small' | 'default' | 'large'
  id?: string
  placeholder?: string
  disabled?: boolean
  filter?: boolean
}>()

const menu = ref(false)
const isDisabled = computed(() => !!props.disabled)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | boolean | null): void
}>()

const model = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const selectedLabel = computed(() => {
  const selectedItem = props.items.find((item) => item.value === model.value)
  return (
    selectedItem?.label || (props.placeholder ? t(props.placeholder) : '--')
  )
})

const currentColor = computed(() => {
  if (isDisabled.value) return '#9E9E9E'

  const item = props.items.find((i) => i.value === model.value)

  const color = item?.color || props.color
  if (color === 'green' || color === 'success') return '#4CAF50'
  if (color === 'red' || color === 'error') return '#F44336'
  if (color === 'amber' || color === 'warning') return '#FFC107'
  if (color === 'blue') return '#2196F3'
  if (color === 'white') return 'white'
  return color || null
})

const filteredItems = computed(() =>
  props.items.filter(
    (item) => item.value !== '' && item.label !== props.placeholder
  )
)

function onPick(item: ItemOption) {
  if (isDisabled.value || item.disabled) return
  emit('update:modelValue', item.value)
  menu.value = false
}
</script>

<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="true"
    offset-y
    :disabled="isDisabled"
  >
    <template #activator="{ props: activatorProps }">
      <v-sheet
        :id="props.id"
        v-bind="activatorProps"
        :variant="props.variant"
        :size="props.size"
        class="text-caption text-truncate d-flex align-center justify-space-between px-2 py-1"
        :class="[
          !currentColor ||
          currentColor === 'white' ||
          currentColor === '#FFC107'
            ? 'text-black'
            : 'text-white',
        ]"
        :style="{
          backgroundColor: currentColor || 'white',
          borderRadius: '4px',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.6 : 1,
          maxWidth: '200px',
          minWidth: '80px',
          width: filter ? '120px' : '',
          padding: '4px 12px',
          border: !currentColor
            ? '1px solid #BDBDBD'
            : currentColor === 'white'
              ? '1px solid black'
              : 'none',
          pointerEvents: isDisabled ? 'none' : 'auto',
        }"
      >
        {{ selectedLabel }}
        <v-icon
          icon="mdi-menu-down"
          size="16"
          :color="!currentColor ? '#0000008a' : undefined"
        />
      </v-sheet>
    </template>

    <v-list class="bg-white">
      <v-list-item
        v-for="(item, index) in filteredItems"
        :key="index"
        class="bg-white"
        :disabled="!!item.disabled || isDisabled"
        @click="onPick(item)"
      >
        <v-list-item-title
          :class="item.disabled || isDisabled ? 'text-disabled' : ''"
        >
          {{ item.label }}
        </v-list-item-title>

        <template #append>
          <v-icon v-if="model === item.value" icon="mdi-check" size="16" />
        </template>
      </v-list-item>
    </v-list>
  </v-menu>
</template>
